import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Download, ExternalLink, Bookmark, Sparkles } from "lucide-react";
import { absoluteFileUrl } from "@/lib/utils_ext";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

function fileKind(url = "") {
  const u = url.toLowerCase();
  if (u.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(u)) return "image";
  return "unknown";
}

export default function PdfViewerModal({ open, onClose, note, onOpenNote }) {
  const [related, setRelated] = useState([]);
  const [bookmarked, setBookmarked] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!open || !note) return;
    setBookmarked(false);
    // Load related items in the same chapter
    api.get("/related", {
      params: {
        resource_type: "note",
        class_level: note.class_level,
        subject: note.subject,
        chapter: note.chapter,
        exclude_id: note.note_id,
        limit: 6,
      },
    }).then((r) => setRelated(r.data)).catch(() => setRelated([]));

    // Check bookmark state
    if (user) {
      api.get("/bookmarks").then((r) => {
        const has = r.data.some((b) => b.resource_type === "note" && b.resource_id === note.note_id);
        setBookmarked(has);
      }).catch(() => {});
    }
  }, [open, note, user]);

  const toggleBookmark = async () => {
    if (!user) return toast.error("Sign in to bookmark");
    try {
      const r = await api.post("/bookmarks/toggle", { resource_type: "note", resource_id: note.note_id });
      setBookmarked(r.data.status === "added");
      toast.success(r.data.status === "added" ? "Bookmarked" : "Removed bookmark");
    } catch {
      toast.error("Bookmark failed");
    }
  };

  if (!note) return null;
  const url = absoluteFileUrl(note.file_url || "");
  const kind = fileKind(note.file_url);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="pdf-viewer-modal"
        className="max-w-6xl w-[95vw] h-[90vh] p-0 rounded-3xl border-border overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-5 border-b border-border flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">
              {note.chapter || "General"}
              {note.topic ? ` · ${note.topic}` : ""}
            </div>
            <DialogTitle className="font-display text-xl md:text-2xl font-bold pr-8 mt-1 truncate">
              {note.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Class {note.class_level} · {note.subject} · by {note.uploader_name}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0 mr-8">
            <Button
              type="button"
              variant="outline"
              onClick={toggleBookmark}
              data-testid="pdf-bookmark-btn"
              className={`rounded-full h-9 px-3 ${bookmarked ? "bg-primary text-primary-foreground border-primary" : ""}`}
            >
              <Bookmark className="w-4 h-4 mr-1.5" fill={bookmarked ? "currentColor" : "none"} />
              {bookmarked ? "Saved" : "Save"}
            </Button>
            {url && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(url, "_blank")}
                  data-testid="pdf-external-btn"
                  className="rounded-full h-9 px-3"
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Open
                </Button>
                <a href={url} download data-testid="pdf-download-btn">
                  <Button type="button" className="rounded-full h-9 px-3 bg-primary hover:bg-primary/90">
                    <Download className="w-4 h-4 mr-1.5" /> Download
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>

        {/* Body: viewer + related */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 bg-muted/30 overflow-hidden">
            {url ? (
              kind === "pdf" ? (
                <iframe
                  src={`${url}#toolbar=0&navpanes=0`}
                  title={note.title}
                  data-testid="pdf-iframe"
                  className="w-full h-full bg-white"
                />
              ) : kind === "image" ? (
                <div className="w-full h-full overflow-auto flex items-center justify-center bg-white">
                  <img src={url} alt={note.title} data-testid="pdf-image" className="max-w-full h-auto" />
                </div>
              ) : (
                <FallbackContent note={note} url={url} />
              )
            ) : (
              <FallbackContent note={note} url={null} />
            )}
          </div>

          {/* Related sidebar */}
          <aside
            data-testid="pdf-related-sidebar"
            className="hidden lg:block w-72 border-l border-border overflow-y-auto p-5 bg-background"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              <Sparkles className="w-3 h-3 text-primary" /> Related notes
            </div>
            {related.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nothing else in this chapter yet.</div>
            ) : (
              <div className="space-y-2">
                {related.map((r) => (
                  <button
                    key={r.note_id}
                    onClick={() => onOpenNote?.(r)}
                    data-testid={`related-note-${r.note_id}`}
                    className="w-full text-left p-3 rounded-2xl hover:bg-muted transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-primary" strokeWidth={1.8} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
                          {r.chapter || "General"}
                        </div>
                        <div className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                          {r.title}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FallbackContent({ note, url }) {
  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="max-w-lg text-center">
        <FileText className="w-14 h-14 text-primary mx-auto mb-4 opacity-60" />
        {note.content ? (
          <div className="text-left text-sm text-foreground/90 whitespace-pre-wrap glass rounded-2xl p-5">
            {note.content}
          </div>
        ) : (
          <div className="text-muted-foreground">No preview available for this file type.</div>
        )}
        {url && (
          <Button
            className="mt-6 rounded-full h-11 px-6 bg-primary hover:bg-primary/90"
            onClick={() => window.open(url, "_blank")}
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Open in a new tab
          </Button>
        )}
      </div>
    </div>
  );
}
